// frontend/src/components/wizard/StepReview/DryRunModal.jsx
import React, { useState, useEffect } from 'react';
import { Beaker, X, Loader2, AlertTriangle, CheckCircle, Database, Settings2 } from 'lucide-react';
import { useLanguageStore } from '../../../store/useLanguageStore';

// Motores SQL que soportan "Simular Carga en BD" (simulate_db=true).
// Valores = sec_engine.name exacto (lowercase canónico).
// MongoDB excluido: la simulación con rollback solo está implementada para SQL.
const SQL_SIMULATE_ENGINES = ['mysql', 'postgres', 'oracle', 'mssql', 'sap_hana'];


export const DryRunModal = ({ dryRun, setDryRun, dryRunLimit, target, mapping, handleSimulateDB, qualityData }) => {
  const [activeTooltip, setActiveTooltip] = useState(null);
  const { t } = useLanguageStore();

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') setDryRun(prev => ({ ...prev, isOpen: false }));
    };
    if (dryRun.isOpen) window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [dryRun.isOpen, setDryRun]);

  if (!dryRun.isOpen) return null;

  // BUG FIX: antes usaba target.type.toUpperCase() con lista en mayúsculas,
  // que no coincidía con sec_engine.name (lowercase: "postgres", "mssql"...).
  const canSimulateDB = SQL_SIMULATE_ENGINES.includes(target?.type?.toLowerCase()?.trim());

  // b) Mapa target → targetAlias para mostrar nombres personalizados en headers.
  // Para archivos planos el usuario puede renombrar cada columna en StepMapping.
  // const aliasMap = Object.fromEntries(
  //   (mapping || []).map(m => [m.target, m.targetAlias || m.target])
  // );

  const getRulesTooltip = (colName) => {
    const fieldMap = mapping.find(m => m.target === colName);
    if (!fieldMap) return t('wizard.review.dry_run.unmapped') || 'Sin mapeo';

    let tooltip = '';
    if (fieldMap.type === 'direct' || fieldMap.type === 'transform') {
      tooltip += `${t('wizard.review.data_flow.source')}: [${fieldMap.source}]\n------------------------\n`;
    }
    if (!fieldMap.pipeline || fieldMap.pipeline.length === 0) {
      return tooltip + (t('wizard.review.dry_run.exact_copy') || 'Copia exacta');
    }

    const getHumanReadableRule = (rule) => {
      const p = rule.params || {};
      // d) FIX: t() retorna la key como string (truthy) cuando no existe la clave i18n,
      // por lo que el || fallback nunca se ejecutaba y aparecían {{variables}} sin resolver.
      // Solución: para reglas con parámetros, siempre construir el string con valores reales.
      const tRule = t(`wizard.mapping.rule_names.${rule.id}`) || rule.label || rule.id;

      switch (rule.id) {
        case 'SET_FIXED':
          return `Valor fijo: "${p.value ?? ''}"`;
        case 'ADVANCED_COND': {
          const opLabels = {
            'equals': '=', 'not_equals': '≠', 'greater_than': '>', 'less_than': '<',
            'contains': 'contiene', 'IS_EMPTY': 'vacío', 'NOT_EMPTY': 'no vacío',
            'EQUALS': '=', 'NOT_EQUALS': '≠', 'GREATER': '>', 'LESS': '<',
            'CONTAINS': 'contiene', 'NOT_CONTAINS': 'no contiene',
            'STARTS_WITH': 'empieza con', 'ENDS_WITH': 'termina con',
          };
          const op = opLabels[p.operator] || p.operator;
          const elseLabel = p.else_action === 'NULL' ? 'NULL'
            : p.else_action === 'EMPTY' ? '(vacío)'
              : p.else_action === 'FIXED' ? `"${p.else_result ?? ''}"`
                : 'Original';
          if (p.operator === 'IS_EMPTY' || p.operator === 'NOT_EMPTY') {
            return `Si [${op}] ➔ "${p.result ?? ''}" (else ➔ ${elseLabel})`;
          }
          return `Si [${op} "${p.condition_val ?? ''}"] ➔ "${p.result ?? ''}" (else ➔ ${elseLabel})`;
        }
        case 'REPLACE':
          return `Reemplazar "${p.search ?? ''}" por "${p.replace ?? ''}"`;
        case 'ADD_PREFIX':
          return `Prefijo: "${p.text ?? ''}"`;
        case 'ADD_SUFFIX':
          return `Sufijo: "${p.text ?? ''}"`;
        case 'TRUNCATE':
          return `Máx ${p.max_length ?? '?'} caracteres`;
        case 'PAD_LEFT':
          return `Rellenar hasta ${p.total_length ?? '?'} caracteres`;
        case 'SUBSTRING':
          return `Extraer posición ${p.start ?? '?'} a ${p.end ?? '?'}`;
        case 'EXTRACT_END':
          return `Últimos ${p.length ?? '?'} caracteres`;
        case 'SPLIT_GET':
          return `Extraer ${p.part === 'BEFORE' ? 'antes' : 'después'} de "${p.delimiter ?? ''}"`;
        case 'MATH_MULTIPLY':
          return `${tRule} ×${p.value ?? '?'}`;
        case 'MATH_DIVIDE':
          return `${tRule} ÷${p.value ?? '?'}`;
        case 'MATH_ROUND':
          return `Redondear a ${p.decimals ?? '0'} decimales`;
        case 'MATH_ADD_SUB':
          return `Sumar/restar ${p.value ?? '?'}`;
        case 'FORMAT_DATE':
          return `Fecha: ${p.from ?? '?'} ➔ ${p.to ?? '?'}`;
        case 'DATE_EXTRACT':
          return `Extraer ${p.part === 'YEAR' ? 'Año' : p.part === 'MONTH' ? 'Mes' : 'Día'}`;
        case 'DEFAULT_NULL':
          return `Nulo/vacío → "${p.value ?? ''}"`;
        default:
          return tRule;
      }
    };

    tooltip += fieldMap.pipeline.map((r, i) => `${i + 1}. ${getHumanReadableRule(r)}`).join('\n');
    return tooltip;
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-in fade-in">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
          <div>
            <h3 className="text-xl font-bold text-slate-800 flex items-center">
              <Beaker className="w-6 h-6 mr-2 text-blue-600" />
              {t('wizard.review.dry_run.title') || 'Simulación de Pipeline ETL'}
            </h3>
            <p className="text-sm text-slate-500 mt-1">
              {t('wizard.review.dry_run.subtitle') || 'Vista previa en memoria (No afecta la BD destino).'}
            </p>
          </div>
          <button
            onClick={() => setDryRun(prev => ({ ...prev, isOpen: false }))}
            className="p-2 text-slate-400 hover:text-slate-700 rounded-full transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 flex-1 overflow-auto bg-slate-100/50">

          {dryRun.isLoading ? (
            <div className="h-64 flex flex-col items-center justify-center text-slate-500">
              <Loader2 className="w-10 h-10 animate-spin text-blue-600 mb-4" />
              <p className="font-bold animate-pulse">
                {t('wizard.review.dry_run.loading') || 'Ejecutando motor ETL...'}
              </p>
            </div>

          ) : dryRun.error ? (
            <div className="bg-red-50 text-red-700 p-6 rounded-xl border border-red-200 flex flex-col items-center justify-center h-64">
              <AlertTriangle className="w-12 h-12 text-red-500 mb-3" />
              <p className="font-bold text-lg mb-1">{t('wizard.review.dry_run.failed') || 'La simulación falló'}</p>
              <p className="text-sm opacity-80">{dryRun.error}</p>
            </div>

          ) : dryRun.data ? (
            <div className="space-y-4 h-full flex flex-col">

              {/* Tabs + Botón Simular en BD */}
              <div className="flex flex-col sm:flex-row justify-between gap-4">
                <div className="flex bg-white rounded-lg p-1 shadow-sm border border-slate-200 w-max">
                  <button
                    onClick={() => setDryRun(prev => ({ ...prev, activeTab: 'original' }))}
                    className={`px-6 py-2 text-sm font-bold rounded-md transition ${dryRun.activeTab === 'original'
                      ? 'bg-indigo-50 text-indigo-700 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                      }`}
                  >
                    {t('wizard.review.dry_run.tab_original') || '1. Datos de Origen'}
                  </button>
                  <button
                    onClick={() => setDryRun(prev => ({ ...prev, activeTab: 'transformed' }))}
                    className={`px-6 py-2 text-sm font-bold rounded-md flex items-center transition ${dryRun.activeTab === 'transformed'
                      ? 'bg-emerald-50 text-emerald-700 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                      }`}
                  >
                    <CheckCircle className="w-4 h-4 mr-1.5" />
                    {t('wizard.review.dry_run.tab_final') || '2. Resultado Final'}
                  </button>
                </div>

                {/* BUG FIX: usa sec_engine.name lowercase via canSimulateDB */}
                {canSimulateDB && (
                  <button
                    onClick={handleSimulateDB}
                    className="flex items-center text-sm font-bold bg-slate-800 text-white px-5 py-2.5 rounded-lg hover:bg-slate-700 transition shadow-md"
                  >
                    <Database className="w-4 h-4 mr-2" />
                    {t('wizard.review.dry_run.sim_db') || 'Simular Carga en BD'}
                  </button>
                )}
              </div>

              {/* Auto-Cast report */}
              {dryRun.data?.autoCast?.length > 0 && dryRun.activeTab === 'transformed' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 flex items-start gap-3">
                  <Settings2 className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-blue-700">
                    <span className="font-bold">{t('wizard.review.dry_run.auto_cast_title') || 'Conversión automática de tipos'}:</span>
                    {' '}
                    {dryRun.data.autoCast.map((ac, i) => (
                      <span key={i}>
                        <span className="font-mono bg-blue-100 px-1 rounded">{ac.column}</span>
                        <span className="text-blue-400 mx-1">→</span>
                        <span>{ac.cast === 'boolean' ? 'Boolean' : ac.cast === 'json' ? 'JSON' : `NULL si vacío (${ac.type})`}</span>
                        {i < dryRun.data.autoCast.length - 1 && <span className="text-blue-300 mx-2">|</span>}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Tabla de datos */}
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden flex-1 flex flex-col">
                <div className="overflow-x-auto max-h-[500px]">
                  <table className="w-full text-left text-sm whitespace-nowrap">
                    <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-[50]">
                      <tr>
                        {(dryRun.activeTab === 'original'
                          ? Object.keys(dryRun.data.original[0] || {})
                          : Object.keys(dryRun.data.transformed[0] || {})
                        ).map((col, idx, arr) => {
                          const fieldMap = mapping.find(m => m.target === col);
                          const isMapped = dryRun.activeTab === 'transformed' && fieldMap;
                          const hasRules = isMapped && fieldMap.pipeline?.length > 0;
                          const isRightEdge = idx >= arr.length - 2;

                          return (
                            <th
                              key={idx}
                              className="px-4 py-3 font-bold text-slate-700 text-xs tracking-wider border-r border-slate-200 last:border-r-0 relative"
                            >
                              <div
                                className={`flex items-center w-max ${isMapped ? 'cursor-help' : ''}`}
                                onClick={() => isMapped && setActiveTooltip(activeTooltip === col ? null : col)}
                                onMouseEnter={() => isMapped && setActiveTooltip(col)}
                                onMouseLeave={() => setActiveTooltip(null)}
                              >
                                {/* {dryRun.activeTab === 'transformed' ? (aliasMap[col] || col) : col} */}
                                {col}
                                {hasRules && <Settings2 className="w-3 h-3 ml-1.5 text-indigo-500" />}
                              </div>
                              {activeTooltip === col && isMapped && (
                                <div className={`absolute z-[100] top-full mt-2 w-[260px] max-w-[85vw] p-3 bg-slate-800 text-white text-[11px] leading-relaxed rounded-xl shadow-2xl font-normal normal-case whitespace-pre-wrap text-left ${isRightEdge ? 'right-0' : 'left-0'}`}>
                                  {getRulesTooltip(col)}
                                  <div className={`absolute -top-1.5 w-3 h-3 bg-slate-800 rotate-45 ${isRightEdge ? 'right-4' : 'left-4'}`} />
                                </div>
                              )}
                            </th>
                          );
                        })}
                        {dryRun.activeTab === 'transformed' && (qualityData || []).length > 0 && (
                          <th className="px-3 py-3 font-bold text-emerald-700 text-xs tracking-wider border-l border-slate-200 text-center whitespace-nowrap bg-emerald-50/50">
                            {t('data_rules.preview_quality')}
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {(dryRun.activeTab === 'original' ? dryRun.data.original : dryRun.data.transformed).map((row, i) => (
                        <tr key={i} className="hover:bg-slate-50/50">
                          {Object.values(row).map((val, idx) => (
                            <td key={idx} className="px-4 py-3 text-slate-600 font-mono text-[13px] border-r border-slate-100 last:border-r-0">
                              {val === null
                                ? <span className="text-slate-300 italic">null</span>
                                : typeof val === 'object'
                                  ? <span className="text-slate-400 text-[11px]">{JSON.stringify(val).slice(0, 80)}</span>
                                  : String(val)}
                            </td>
                          ))}
                          {dryRun.activeTab === 'transformed' && (qualityData || []).length > 0 && (
                            <td className="px-3 py-3 text-center border-l border-slate-200">
                              {(() => {
                                const violations = (qualityData || [])[i] || [];
                                if (violations.length === 0) {
                                  return <span className="text-emerald-500 text-sm" title={t('data_rules.preview_pass')}>✅</span>;
                                }
                                const hasBlock = violations.some(v => v.onViolation === 'block');
                                const tooltip = violations.map(v => `${v.field}: ${t('data_rules.rule_' + v.ruleType)}`).join('\n');
                                return (
                                  <span
                                    className={`text-sm cursor-help ${hasBlock ? 'text-red-500' : 'text-amber-500'}`}
                                    title={tooltip}
                                  >
                                    {hasBlock ? '❌' : '⚠️'} {violations.length}
                                  </span>
                                );
                              })()}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {dryRun.data.original.length === 0 && (
                    <div className="p-8 text-center text-slate-500">
                      {t('wizard.review.dry_run.no_data') || 'No hay datos en la tabla de origen para probar.'}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};
